import { useEffect, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';

interface Props {
    repoId: string;
    path: string;
    quote: string;
    onClose: () => void;
}

interface ExplainResponse {
    answer: string;
    usage: { promptTokens: number; completionTokens: number; model: string; usdCost: number };
    sources?: Array<{ n: number; path: string; title?: string }>;
}

export function ExplainModal({ repoId, path, quote, onClose }: Props) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ExplainResponse | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [owner, name] = repoId.split('/');
                const r = await apiCall<ExplainResponse>(
                    `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/ai/explain`,
                    { method: 'POST', body: { path, quote } }
                );
                if (!cancelled) setResult(r);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof ApiError ? err.message : (err as Error).message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [repoId, path, quote]);

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>Explain this passage</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                <div className="modal-body">
                    <blockquote className="comment__quote">{quote}</blockquote>
                    {loading && <div className="comment-panel__loading">Asking AI…</div>}
                    {error && <div className="form-error">{error}</div>}
                    {result && (
                        <>
                            <p className="comment__body">{result.answer}</p>
                            {result.sources && result.sources.length > 0 && (
                                <div className="ai-sources">
                                    <strong>Sources cited:</strong>
                                    <ul>
                                        {result.sources.map((s) => (
                                            <li key={s.n}>
                                                [{s.n}] <code>{s.path}</code>{s.title ? ` — ${s.title}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <p className="form-hint">
                                {result.usage.model} · {result.usage.promptTokens} in / {result.usage.completionTokens} out ·
                                ${result.usage.usdCost.toFixed(4)}
                            </p>
                        </>
                    )}
                </div>
                <footer className="modal-footer">
                    <button className="btn btn--primary" onClick={onClose}>Close</button>
                </footer>
            </div>
        </div>
    );
}
